import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, RefreshCw } from "lucide-react";
import { useDashboardUI } from "../context/GlobalContext";
import CreateCampaignDrawer from "../components/campaigns/CreateCampaignDrawer";
import campaignService from "../services/campaignService";
import { decodeAccessToken } from "../helpers/helpers";

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const { selectedFacilityFilter, setPageLoader } = useDashboardUI();
  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const intervalRef = useRef(null);

  const fetchCampaigns = async () => {
    try {
      setPageLoader(true);

      // const params = new URLSearchParams({
      //   page: page,
      //   limit: limit,
      // });

      // if (debouncedSearch) {
      //   params.append("search", debouncedSearch);
      // }

      // let queryString = `?${params.toString()}`;
      let queryString = `?facility_id=`;
      if (selectedFacilityFilter?.facility_number) {
        queryString += `${selectedFacilityFilter.facility_number}`;
      } else {
        queryString += `${decodeAccessToken()?.facility_id || ""}`;
      }
      const responseData = await campaignService.getAllCampaigns(queryString);
      const responseDataParsed = responseData.data;
      const campaignsData =
        responseDataParsed.records || [];
      const totalCount =
        responseDataParsed.pagination?.totalRecords ?? campaignsData.length;
      const totalPagesCount =
        (responseDataParsed.pagination?.totalPages ?? Math.ceil(totalCount / limit)) || 1;
      setCampaigns(campaignsData);
      setTotal(totalCount);
      setTotalPages(totalPagesCount);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch campaigns:", err);
      setError("Failed to load campaigns.");
    } finally {
      setLoading(false);
      setPageLoader(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch campaigns when filters or pagination change
  useEffect(() => {
    fetchCampaigns();
  }, [page, limit, debouncedSearch, selectedFacilityFilter]);

  // Background refresh every 1 minute
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      fetchCampaigns();
    }, 60000);
    return () => clearInterval(intervalRef.current);
  }, [page, limit, debouncedSearch, selectedFacilityFilter]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchCampaigns();
    setIsRefreshing(false);
  };

  const handleDrawerClose = (refresh) => {
    setIsDrawerOpen(false);
    if (refresh) {
      setPage(1);
      fetchCampaigns();
    }
  };

  const getStatusPillStyle = (status) => {
    // Soft tinted pills, same palette as the other tables
    const green = { background: "#E7F7EE", color: "#12945A" };
    const blue = { background: "#E8EAFB", color: "#3F4BB8" };
    const amber = { background: "#FEF3C7", color: "#B45309" };
    const gray = { background: "#F1F2F7", color: "#4B5266" };
    const red = { background: "#FDECEC", color: "#D93636" };

    if (!status) return gray;

    const normalized = status.toLowerCase().replace(/[\s_-]/g, "");

    const greenStatuses = ["completed", "delivered", "sent", "finished"];
    const blueStatuses = ["running", "inprogress", "processing", "scheduled"];
    const amberStatuses = ["pending", "draft", "queued"];
    const redStatuses = ["failed", "error", "cancelled", "canceled"];

    if (greenStatuses.includes(normalized)) return green;
    if (blueStatuses.includes(normalized)) return blue;
    if (amberStatuses.includes(normalized)) return amber;
    if (redStatuses.includes(normalized)) return red;
    return gray;
  };

  const getModeBadge = (mode) => {
    if (!mode) return null;
    const upper = mode.toUpperCase();
    const style =
      upper === "SMS"
        ? "bg-[#E8EAFB] text-[#3F4BB8]"
        : "bg-[#F3EAFE] text-[#7C3AED]";
    return (
      <span
        className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${style}`}
      >
        {upper}
      </span>
    );
  };

  return (
    <div className="w-full flex flex-col h-full lg:h-[calc(100vh-100px)] pt-4 gap-3">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
        <div>
          {/* <input
          type="text"
          placeholder="Search by campaign name or owner"
          value={search}
          onChange={(e) => {
            const newSearch = e.target.value;
            if (newSearch) {
              setPage(1);
            }
            setSearch(newSearch);
          }}
          className="w-80 rounded-full border border-gray-200 px-5 py-2.5 text-sm display-none"
        /> */}
        </div>
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh"
            aria-label="Refresh"
            className="flex flex-shrink-0 items-center justify-center gap-2 w-11 sm:w-auto sm:px-4 h-11 text-sm font-medium text-[#4B5266] rounded-xl border border-[#ECEEF4] bg-white hover:border-[#D9DCE6] hover:bg-gray-50 transition disabled:opacity-50"
          >
            <RefreshCw size={14} className={isRefreshing ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={() => setIsDrawerOpen(true)}
            className="flex flex-shrink-0 items-center gap-2 px-4 sm:px-6 h-11 text-sm font-semibold whitespace-nowrap rounded-xl bg-[#3F4BB8] text-white hover:bg-[#363FA0] transition"
          >
            <Plus size={16} />
            Create<span className="hidden sm:inline"> Campaign</span>
          </button>
        </div>
      </div>

      {/* Table card (same look as Feedback Items / User Management / Support Tickets) */}
      <div className="flex-1 min-h-0 bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
        <div className="table-scroll h-full overflow-auto">
        <table className="w-full min-w-[800px] table-fixed border-collapse text-sm text-left">
          {/* Explicit column widths so the header and every row share the exact same columns */}
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "18%" }} />
          </colgroup>
          <thead>
            <tr className="border-b border-gray-100">
              {[
                { label: "Name", width: "30%" },
                { label: "Mode", width: "14%" },
                { label: "Total Records", width: "20%" },
                { label: "Created On", width: "18%" },
                { label: "Status", width: "18%" },
              ].map(({ label, width }) => (
                <th
                  key={label}
                  style={{
                    position: "sticky", top: 0, background: "#F5F2FF", zIndex: 20, width, textAlign: "left",
                    // Total Records header sits a little to the left of its values
                    ...(label === "Total Records" || label === "Created On" ? { paddingLeft: "8px" } : {}),
                  }}
                  className="py-3.5 pl-6 pr-3 align-middle text-xs font-semibold text-gray-500 uppercase tracking-wide"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-10 text-center text-sm text-gray-500"
                >
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2B3674] mr-3"></div>
                    Loading campaigns...
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-10 text-center text-sm text-red-600"
                >
                  {error}
                </td>
              </tr>
            ) : campaigns.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-10 text-center text-sm text-gray-500"
                >
                  No campaigns found
                </td>
              </tr>
            ) : (
              campaigns.map((campaign) => (
                <tr
                  key={campaign.id || campaign._id}
                  className="cursor-pointer border-b border-gray-100 last:border-0 transition-colors hover:bg-gray-50"
                  onClick={() => navigate(`/campaigns/${campaign.id || campaign._id}`)}
                >
                  <td className="pl-6 pr-3 py-4 align-middle text-left text-sm font-semibold text-[#14122B]">
                    <div className="line-clamp-1" title={campaign.name}>
                      {campaign.name || "-"}
                    </div>
                  </td>

                  <td className="pl-6 pr-3 py-4 align-middle text-left">
                    {getModeBadge(campaign.mode)}
                  </td>

                  <td className="pl-10 pr-3 py-4 align-middle text-left text-sm font-medium text-gray-600 tabular-nums">
                    {campaign.totalRecords ||  "-"}
                  </td>

                  <td className="pl-6 pr-3 py-4 align-middle text-left text-sm font-medium text-gray-600 whitespace-nowrap">
                    {campaign.createdOn
                      ? new Date(
                          campaign.createdOn
                        ).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : "-"}
                  </td>

                  <td className="pl-6 pr-3 py-4 align-middle text-left">
                    <span
                      style={{
                        ...getStatusPillStyle(campaign.status),
                        borderRadius: "999px",
                        padding: "0.25rem 0.75rem",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        lineHeight: 1.4,
                        whiteSpace: "nowrap",
                        display: "inline-block",
                        textTransform: "capitalize",
                      }}
                    >
                      {campaign.status || "-"}
                    </span>
                  </td>

                  {/* <td className="w-[15%] rounded-r-xl px-6 py-4 text-gray-500">
                    {campaign.to || "-"}
                  </td> */}
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination (same layout as Feedback Items / User Management) */}
      <div className="mt-auto pt-1 pb-1 flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap text-sm flex-shrink-0">
        <span>Total {total}</span>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            Rows per page
            <select
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
              className="rounded-md border px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-50"
            >
              &lsaquo;
            </button>

            <span>
              {page} / {totalPages}
            </span>

            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl disabled:opacity-50"
            >
              &rsaquo;
            </button>
          </div>
        </div>
      </div>

      {/* Create Campaign Drawer */}
      <CreateCampaignDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
      />
    </div>
  );
};

export default Campaigns;
